import React, { useState, useEffect } from 'react';
import { RadiatorElement, RadiatorType } from '../types';

interface RadiatorDimensionPromptProps {
  isOpen: boolean;
  radiatorElement: RadiatorElement | null;
  onSave: (dimensions: { width: number; height: number; radiatorType: RadiatorType; }) => void;
  onDelete: () => void;
  onClose: () => void;
  isFullscreen?: boolean;
}

const RadiatorDimensionPrompt: React.FC<RadiatorDimensionPromptProps> = ({ isOpen, radiatorElement, onSave, onDelete, onClose, isFullscreen = false }) => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [radiatorType, setRadiatorType] = useState<RadiatorType>('with-trvs');

  useEffect(() => {
    if (radiatorElement) {
      // Convert from pixels to meters (divided by 1000 for display)
      // Only populate if values are not the default presets
      const widthInMeters = radiatorElement.width ? radiatorElement.width / 1000 : 0;
      const heightInMeters = radiatorElement.height ? radiatorElement.height / 1000 : 0;
      
      // Leave blank instead of showing 0.08 and 0.01 presets
      setWidth(widthInMeters && widthInMeters !== 0.08 ? widthInMeters.toFixed(2) : '');
      setHeight(heightInMeters && heightInMeters !== 0.01 ? heightInMeters.toFixed(2) : '');
      setRadiatorType(radiatorElement.radiatorType || 'with-trvs');
    }
  }, [radiatorElement]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    onSave({
      width: parseFloat(width) || 0,
      height: parseFloat(height) || 0,
      radiatorType: radiatorType,
    });
    onClose();
  };

  const handleDelete = () => {
    onDelete();
  };

  const formatDecimalInput = (value: string): string => {
    if (value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return numValue.toString();
  };

  // Modern dropdown component with icons and enhanced styling
  const ModernDropdown = ({ label, value, options, onChange, icon }: {
    label: string;
    value: string;
    options: Array<{value: string; label: string}>;
    onChange: (value: string) => void;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 appearance-none cursor-pointer hover:border-slate-300 shadow-sm"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );

  const radiatorTypeOptions = [
    { value: 'with-trvs', label: 'Radiator with TRVs' },
    { value: 'without-trvs', label: 'Radiator without TRVs' },
    { value: 'panel-heater', label: 'Panel Heater' },
    { value: 'fan-heater', label: 'Fan Heater' },
    { value: 'electric-storage-heater', label: 'Electric Storage Heater' },
    { value: 'high-heat-retention', label: 'High Heat Retention' }
  ];

  return (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[1000]'}`}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden border border-slate-200/50">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-orange-600 via-orange-500 to-red-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Heating System Details</h3>
                <p className="text-orange-100 text-xs">Configure specifications</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-white/20 text-white px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm">
                Floor {radiatorElement?.floor}
              </span>
            </div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 via-white/5 to-red-600/0 pointer-events-none"></div>
        </div>

        {/* Content Area */}
        <div className="px-5 py-5 space-y-5 max-h-[calc(85vh-140px)] overflow-y-auto">
          {/* Dimensions Section */}
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              Dimensions
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="radiator-length" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span>Length (mm)</span>
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  id="radiator-length"
                  value={width}
                  onChange={(e) => setWidth(formatDecimalInput(e.target.value))}
                  placeholder="600"
                  min="100"
                  max="3000"
                  step="10"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="radiator-height" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                  </svg>
                  <span>Height (mm)</span>
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  id="radiator-height"
                  value={height}
                  onChange={(e) => setHeight(formatDecimalInput(e.target.value))}
                  placeholder="600"
                  min="200"
                  max="1000"
                  step="10"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Radiator Type */}
          <ModernDropdown
            label="Radiator Type"
            value={radiatorType}
            options={radiatorTypeOptions}
            onChange={(value) => setRadiatorType(value as RadiatorType)}
            icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>}
          />
        </div>

        {/* Modern Action Bar */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex justify-between items-center">
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all duration-200"
            >
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
            <div className="flex space-x-2">
              <button
                onClick={onClose}
                className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg hover:from-orange-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500/20 shadow-lg shadow-orange-500/25 transition-all duration-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadiatorDimensionPrompt; 